import React from 'react';
import {
  AlignLeft, Bell, Briefcase, CalendarDays, Check, CircleHelp, Clock, Copy, ExternalLink, Lock, MapPin,
  Pencil, Repeat, Trash2, User, Users, Video, X, CalendarCheck,
} from 'lucide-react';
import { enlaceMeet } from '../../lib/meetEnlace';
import { toast } from 'sonner';
import {
  ESTADOS, esUrl, textoCuando, textoRecordatorio, trozosConEnlaces,
} from '../../lib/calendario';
import { initials } from '../../lib/format';

const ICONO_ESTADO = {
  aceptado: { c: 'var(--good)', t: '✓' },
  rechazado: { c: 'var(--danger)', t: '✕' },
  quizas: { c: 'var(--warn)', t: '?' },
  pendiente: { c: 'var(--text-3)', t: '' },
};

/** Lo que se ve al pulsar un evento. Las acciones las decide `ev.editable` / `editable_parcial` del servidor. */
export default function DetalleEvento({
  ev, tz, calendario, color, onCerrar, onEditar, onBorrar, onResponder, onDuplicar, respondiendo,
}) {
  if (!ev) return null;
  if (ev.solo_ocupado) {
    return (
      <div className="cal-detalle" data-testid="cal-detalle">
        <div className="cal-detalle-barra">
          <span className="flex-1" />
          <button type="button" className="cal-icono-btn" onClick={onCerrar} aria-label="Cerrar"><X className="h-4 w-4" /></button>
        </div>
        <div className="cal-detalle-fila cal-detalle-fila--titulo">
          <span className="cal-cuadro" style={{ background: color }} />
          <div>
            <h3 className="cal-detalle-titulo">Ocupado</h3>
            <p className="cal-detalle-sub">{textoCuando(ev, tz)}</p>
          </div>
        </div>
        {calendario && (
          <div className="cal-detalle-fila"><CalendarDays className="cal-detalle-ico" /><span>{calendario.nombre}</span></div>
        )}
        <p className="cal-detalle-nota">Solo puedes ver que esta persona está ocupada, no los detalles.</p>
      </div>
    );
  }

  const invitados = ev.invitados || [];
  const cuenta = invitados.reduce((a, i) => ({ ...a, [i.estado]: (a[i.estado] || 0) + 1 }), {});
  const soyInvitado = !!ev.mi_estado && !ev.soy_organizador;
  const puedeEditar = ev.editable || ev.editable_parcial;

  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(texto); toast.success('Copiado'); } catch { toast.error('No se pudo copiar'); }
  };

  return (
    <div className="cal-detalle" data-testid="cal-detalle">
      <div className="cal-detalle-barra">
        {puedeEditar && (
          <button type="button" className="cal-icono-btn" onClick={onEditar} aria-label="Editar evento" title="Editar evento" data-testid="cal-detalle-editar">
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {(ev.editable || ev.editable_parcial) && (
          <button type="button" className="cal-icono-btn" onClick={onBorrar} aria-label="Eliminar evento" title={ev.editable_parcial ? 'Quitar de mi calendario' : 'Eliminar evento'} data-testid="cal-detalle-borrar">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        {ev.editable && onDuplicar && (
          <button type="button" className="cal-icono-btn" onClick={onDuplicar} aria-label="Duplicar" title="Duplicar">
            <Copy className="h-4 w-4" />
          </button>
        )}
        <span className="flex-1" />
        <button type="button" className="cal-icono-btn" onClick={onCerrar} aria-label="Cerrar" data-testid="cal-detalle-cerrar"><X className="h-4 w-4" /></button>
      </div>

      <div className="cal-detalle-cuerpo od-scroll">
        <div className="cal-detalle-fila cal-detalle-fila--titulo">
          <span className="cal-cuadro" style={{ background: color }} />
          <div className="min-w-0">
            <h3 className="cal-detalle-titulo">{ev.titulo || '(Sin título)'}</h3>
            <p className="cal-detalle-sub">{textoCuando(ev, tz)}</p>
            {ev.repeticion && <p className="cal-detalle-sub"><Repeat className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />{ev.repeticion}</p>}
            {!ev.repeticion && ev.serie_id && <p className="cal-detalle-sub"><Repeat className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />Forma parte de un evento periódico (esta fecha se cambió aparte)</p>}
            {ev.zona && ev.zona !== tz && !ev.todo_el_dia && (
              <p className="cal-detalle-sub">Zona del evento: {ev.zona.replace(/_/g, ' ')}</p>
            )}
          </div>
        </div>

        {ev.cita_id && (
          <div className="cal-detalle-fila"><CalendarCheck className="cal-detalle-ico" /><span>Cita reservada desde una página de reserva</span></div>
        )}
        {ev.origen === 'invitacion_externa' && (
          <div className="cal-detalle-fila"><ExternalLink className="cal-detalle-ico" /><span>Invitación recibida por correo: solo puedes cambiar tus recordatorios, color y disponibilidad.</span></div>
        )}

        {enlaceMeet(ev.ubicacion, ev.descripcion) && (
          <div className="cal-detalle-fila" data-testid="cal-detalle-meet">
            <Video className="cal-detalle-ico" />
            <div className="cal-meet">
              <a className="cal-meet-boton" href={enlaceMeet(ev.ubicacion, ev.descripcion).url} target="_blank" rel="noopener noreferrer">Unirse con Orvian Meet</a>
              <span className="cal-meet-url">{enlaceMeet(ev.ubicacion, ev.descripcion).url.replace(/^https?:\/\//, '')}</span>
            </div>
          </div>
        )}
        {ev.ubicacion && !(enlaceMeet(ev.ubicacion) && enlaceMeet(ev.ubicacion).url === ev.ubicacion.trim()) && (
          <div className="cal-detalle-fila">
            <MapPin className="cal-detalle-ico" />
            {esUrl(ev.ubicacion) ? (
              <a href={ev.ubicacion} target="_blank" rel="noopener noreferrer" className="cal-enlace break-all">{ev.ubicacion}</a>
            ) : (
              <span className="break-words">{ev.ubicacion}</span>
            )}
          </div>
        )}

        {invitados.length > 0 && (
          <div className="cal-detalle-fila cal-detalle-fila--arriba">
            <Users className="cal-detalle-ico" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{invitados.length + 1} {invitados.length + 1 === 1 ? 'persona' : 'personas'}</div>
              <div className="cal-detalle-sub">
                {[cuenta.aceptado && `${cuenta.aceptado} sí`, cuenta.quizas && `${cuenta.quizas} quizás`, cuenta.rechazado && `${cuenta.rechazado} no`, cuenta.pendiente && `${cuenta.pendiente} pendiente${cuenta.pendiente > 1 ? 's' : ''}`].filter(Boolean).join(', ')}
              </div>
              <ul className="cal-invitados">
                {ev.organizador?.correo && (
                  <li>
                    <span className="cal-avatar">{initials(ev.organizador.nombre, ev.organizador.correo)}
                      <span className="cal-avatar-estado" style={{ background: ICONO_ESTADO.aceptado.c }}>✓</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate">{ev.organizador.nombre || ev.organizador.correo}</span>
                      <span className="block cal-detalle-sub">Organizador</span>
                    </span>
                  </li>
                )}
                {invitados.map((i) => (
                  <li key={i.correo}>
                    <span className="cal-avatar">{initials(i.nombre, i.correo)}
                      {i.estado !== 'pendiente' && <span className="cal-avatar-estado" style={{ background: ICONO_ESTADO[i.estado]?.c }}>{ICONO_ESTADO[i.estado]?.t}</span>}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate" title={i.correo}>{i.nombre || i.correo}</span>
                      <span className="block cal-detalle-sub">
                        {ESTADOS[i.estado] || 'Pendiente'}{i.opcional ? ' · Opcional' : ''}{!i.interno ? ' · Externo' : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {!invitados.length && ev.organizador?.correo && !ev.soy_organizador && (
          <div className="cal-detalle-fila"><User className="cal-detalle-ico" /><span>Organizado por {ev.organizador.nombre || ev.organizador.correo}</span></div>
        )}

        {ev.descripcion && (
          <div className="cal-detalle-fila cal-detalle-fila--arriba">
            <AlignLeft className="cal-detalle-ico" />
            <p className="cal-descripcion">
              {trozosConEnlaces(ev.descripcion).map((t, i) => (t.enlace
                ? <a key={i} href={t.enlace} target="_blank" rel="noopener noreferrer" className="cal-enlace break-all">{t.enlace}</a>
                : <React.Fragment key={i}>{t.texto}</React.Fragment>))}
            </p>
          </div>
        )}

        {ev.recordatorios?.length > 0 && (
          <div className="cal-detalle-fila cal-detalle-fila--arriba">
            <Bell className="cal-detalle-ico" />
            <div>{ev.recordatorios.map((m) => <div key={m}>{textoRecordatorio(m)}</div>)}</div>
          </div>
        )}

        {calendario && (
          <div className="cal-detalle-fila"><CalendarDays className="cal-detalle-ico" /><span>{calendario.nombre}{calendario.dueno ? ` · ${calendario.dueno.nombre}` : ''}</span></div>
        )}
        <div className="cal-detalle-fila">
          <Briefcase className="cal-detalle-ico" />
          <span>{ev.disponibilidad === 'libre' ? 'Libre' : 'Ocupado'}{ev.visibilidad === 'privada' ? ' · Privado' : ''}</span>
          {ev.visibilidad === 'privada' && <Lock className="h-3.5 w-3.5" style={{ color: 'var(--text-3)' }} />}
        </div>
        {ev.ubicacion && !esUrl(ev.ubicacion) && (
          <button type="button" className="cal-mini-enlace" onClick={() => copiar(ev.ubicacion)}>Copiar ubicación</button>
        )}
      </div>

      {soyInvitado && (
        <div className="cal-detalle-pie" data-testid="cal-detalle-responder">
          <span className="cal-detalle-sub flex-1">{ev.mi_estado && ev.mi_estado !== 'pendiente' ? 'Tu respuesta' : '¿Asistirás?'}</span>
          {[
            ['aceptado', 'Sí', Check],
            ['quizas', 'Quizás', CircleHelp],
            ['rechazado', 'No', X],
          ].map(([estado, texto, Ico]) => (
            <button
              key={estado}
              type="button"
              className={`cal-rsvp ${ev.mi_estado === estado ? 'is-activa' : ''}`}
              onClick={() => onResponder(estado)}
              disabled={respondiendo}
              aria-pressed={ev.mi_estado === estado}
              data-testid={`cal-responder-${estado}`}
            >
              <Ico className="h-3.5 w-3.5" />{texto}
            </button>
          ))}
        </div>
      )}
      {!soyInvitado && !puedeEditar && (
        <div className="cal-detalle-pie"><Clock className="h-4 w-4" style={{ color: 'var(--text-3)' }} /><span className="cal-detalle-sub">Solo puedes ver este evento.</span></div>
      )}
    </div>
  );
}
